import { ComponentFactoryResolver, Injectable } from '@angular/core';

import {
  IChatAttachment,
  IMAGE_FILE_FORMATS,
  checkIfFileHeicFormat,
  CieUtilsModalManagerService,
} from '@cakeiteasy/web-utils';

import {
  CROPPED_IMAGE_MAX_SIZE_PX,
  DEFAULT_ASPECT_RATIOS,
  IMAGE_MB_SIZE_LIMIT,
  MOTIVE_MAX_IMAGE_SIZE_PX,
} from '../../constants/common.constants';
import { ImageCropperExtraService } from '../../../image-cropper/image-cropper-extra.service';
import { UiFullSizeImageComponent } from '../../modals/ui-full-size-image/ui-full-size-image.component';
import { UiChooseMotiveModalComponent } from '../ui-cake-build-motive/ui-choose-motive-modal/ui-choose-motive-modal.component';

/**
 * Everything a picked file goes through before it can be attached to a message: acceptance,
 * HEIC conversion, cropping, and upload.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatAttachmentService {
  constructor(
    private _imageCropper: ImageCropperExtraService,
    private _modalManager: CieUtilsModalManagerService,
    private _componentResolver: ComponentFactoryResolver,
  ) {}

  /**
   * Runs one file through the whole pipeline. Returns null when the file is not usable, so the
   * caller can keep going through a multi-file selection instead of failing the batch.
   */
  public async createAttachment(
    file: File,
    upload: (img: string) => Promise<IChatAttachment>,
  ): Promise<IChatAttachment> {
    if (!this._isAcceptable(file)) {
      return null;
    }

    const source: File = checkIfFileHeicFormat(file) ? (await this._imageCropper.convertHeic(file)).body : file;
    const imgSrc = await this._imageCropper.prepareImageForCropping(
      source,
      IMAGE_FILE_FORMATS.JPG,
      false,
      CROPPED_IMAGE_MAX_SIZE_PX,
    );
    const img = await this._showMotiveModal(imgSrc);
    const { id, image } = await upload(img);

    return { id, image };
  }

  public showImage(imgUrl: string) {
    this._modalManager
      .show(UiFullSizeImageComponent, this._componentResolver, { imgUrl }, { windowClass: 'full-size-image-modal' })
      .catch(() => {});
  }

  private _isAcceptable(file: File): boolean {
    if (!file || file.size > IMAGE_MB_SIZE_LIMIT) {
      return false;
    }

    // HEIC files do not always carry an `image/` mime type, so they are checked by signature.
    return /image\//.test(file.type) || checkIfFileHeicFormat(file);
  }

  private _showMotiveModal(url?: string): Promise<string> {
    return this._modalManager.show(
      UiChooseMotiveModalComponent,
      this._componentResolver,
      {
        imgUrl: url,
        aspectRatios: DEFAULT_ASPECT_RATIOS,
        maxImageSize: MOTIVE_MAX_IMAGE_SIZE_PX,
      },
      { backdrop: 'static' },
    );
  }
}
